import React, { useEffect, useState, useTransition } from 'react';

import { GenRouteUrls, Keys, ModuleFieldsData, Modules, ReqUrls } from '../../config/AppConfig';
import { formatDateValue, isCurrUserRoleAdmin, printError, printLog, sendRequest } from "../../utils/AppUtils";
import { useTheme } from '../../../common/ProjectThemeProvider';
import { Doc_CreateFolder_FormDialog, InternalPageHeaderBar, RecordsPagination, ThemedConfirmDialog, ThemedIconButton } from "../../index"
import { Flexbox, Image, Label, LoadingSection, Section, Select } from "../../../common/fusion-ui"
import { CssVariant, DefStrNA } from 'common/fusion-ui/config/FusionConfig';
import {
    checkNullArr,
    checkNullStr,
    dropJsonListDuplicates,
    getArrIndexValue,
    getArrLen,
    getJsonValue,
    getDefStr,
    getHighlightedMatchedText,
    isBoolTrue,
    isJsonValueTrue,
    limitStringWords,
    shuffleList,
    sortJsonListAdvance,
    trim
} from 'common/fusion-ui/utils/ReactUtils';
import { useToast } from "../../../common/fusion-ui/provider/ToastProvider";
import { Link } from "react-router-dom";
import { useConfig } from "../../context/ConfigProvider";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";
import { AnimationType, FramerAnimator } from "../../../common/fusion-ui/animation/FramerAnimator";


const DocPanel = React.memo(({
    recordList = [],
    loadingStatus = {},
    onRecordsUpdate,
    fromSearchPage = false,
    showControls = true,
    showHeader = true,
    showPagination = true,
    searchString,
}) => {
    const TAG = 'DocPanel';

    const moduleName = Modules.Documents;
    const mf = ModuleFieldsData[moduleName];

    const animator = new FramerAnimator();

    const theme = useTheme();
    const { showToast, changeToastVariant } = useToast();
    const { isScreenUpSm } = useScreenSize();
    const { updateConfig } = useConfig();

    const SEARCH_BY_PARAMS = {
        anything: {
            name: 'Anything'
        },
        title: {
            name: mf.title.title
        },
        desc: {
            name: mf.desc.title
        },
        author: {
            name: mf.author.title
        },
        date: {
            name: mf.date.title
        }
    };
    const SORT_BY_PARAMS = {
        title: {
            name: mf.title.title,
            sortKey: mf.title.column,
        },
        desc: {
            name: mf.desc.title,
            sortKey: mf.desc.column,
        },
        author: {
            name: mf.author.title,
            sortKey: mf.author.column,
        },
        date: {
            name: mf.date.title,
            sortKey: mf.date.column,
        },
    };
    const DOC_ICONS = [
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354337_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354338_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354339_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354340_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354341_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354342_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354343_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354344_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354345_1280.png',
        'https://cdn.pixabay.com/photo/2014/05/26/09/44/icon-354346_1280.png',
    ];
    const PDF_ICON = 'https://img.icons8.com/ios/96/228BE6/pdf--v1.png';
    const PPT_ICON = 'https://img.icons8.com/ios/96/228BE6/ppt.png';

    const PAGINATION_RECORD_LIMITS = [4, 8, 12, 16, 20];
    const DEF_PGNTN_RECORD_LIMIT = PAGINATION_RECORD_LIMITS[0];
    const DEF_PGNTN_START_INDEX = 1;
    const DESC_WORD_LIMIT = isScreenUpSm ? 20 : 40;
    const ALL_FOLDERS = `All Folders`;
    const DOC_VECTOR_KEY = `docVector`;

    // use transition hook
    const [sortListPending, startSortListTransition] = useTransition();
    const [searchListPending, startSearchListTransition] = useTransition();

    const Fields = {
        recordList: `${TAG}_recordList`,

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',

        searchInputValue: 'searchInputValue',
        searchBySelectedOption: 'searchBySelectedOption',

        sortBySelectedOption: 'sortBySelectedOption',
        isSortByOrderAsc: 'isSortByOrderAsc',

        pagination_currPageNum: 'pagination_currPageNum',
        pagination_currPageLimit: 'pagination_currPageLimit',

        recordDeleteBtn_Disable: 'recordDeleteBtn_Disable',
        recordDeleteBtn_AsLoading: 'recordDeleteBtn_AsLoading',
        currRecordDeleteId: 'currRecordDeleteId',
        showDeleteDialog: 'showDeleteDialog',

        folders: 'folders',
        selectedFolder: 'selectedFolder',
        openCreateFolderDialog: 'openCreateFolderDialog',

        isSearchedStringSet: 'isSearchedStringSet',
    };
    const [fieldsData, setFieldsData] = useState({});

    useEffect(() => {
        //Initial def-values
        if (!checkNullStr(getField(Fields.searchBySelectedOption))) {
            updateConfig(Keys.tracker_currPageModule, moduleName);
            updateConfig(Keys.tracker_currPageTag, TAG);
            updateField(Fields.searchBySelectedOption, SEARCH_BY_PARAMS.anything.name);
            updateField(Fields.sortBySelectedOption, SORT_BY_PARAMS.title.name);
            updateField(Fields.isSortByOrderAsc, true);
            updateField(Fields.pagination_currPageNum, DEF_PGNTN_START_INDEX);
            updateField(Fields.pagination_currPageLimit, DEF_PGNTN_RECORD_LIMIT);
        }
    }, []);

    useEffect(() => {
        fetchPanelData();
    }, [recordList, loadingStatus, searchString, fromSearchPage]);

    // added by Vandit Tewari on (11/11/24) - made this state to manage the delete process 
    const [isDeleting, setIsDeleting] = useState(false);
    return (<>
        {createPanel()}
        {isBoolTrue(showControls) && createDeleteDialog()}
        {createFolderFormDialog()}
    </>);

    //======================UI Functions start=====================
    function createPanel() {
        return <div className={''}>
            <div className={'position-relative'}>
                {/*header-bar*/}
                {(isBoolTrue(showHeader)) &&
                    <InternalPageHeaderBar
                        className={`${isBoolTrue(fromSearchPage) ? '' : ''}`}
                        bg={isBoolTrue(fromSearchPage) ? 'transparent' : theme.primary10Opaque}
                        px={0}

                        moduleName={moduleName}

                        uploadUrl={isBoolTrue(fromSearchPage) ? '' : GenRouteUrls.doc_uploadPage}
                        recomUrl={isBoolTrue(fromSearchPage) ? '' : GenRouteUrls.doc_recomPage}

                        showSearch={true}
                        showSort={false}
                        searchByParams={SEARCH_BY_PARAMS}
                        sortByParams={SORT_BY_PARAMS}

                        recordsLen={getArrLen(getRecordList())}
                        filteredRecordsLen={getArrLen(getRecordList(true))}
                        searchValue={getField(Fields.searchInputValue)}
                        searchByDefOption={getField(Fields.searchBySelectedOption)}
                        sortByDefOption={getField(Fields.sortBySelectedOption)}
                        isSorting={sortListPending}
                        isSearching={searchListPending}
                        isSortAsc={isFieldTrue(Fields.isSortByOrderAsc)}

                        onUploadClick={(e) => {
                        }}
                        onRecomClick={(e) => {
                        }}
                        onSearchChange={(e, value) => handleSearchInputChange(value)}
                        onSearchBy={(name, item, index) => {
                            updateField(Fields.searchBySelectedOption, name);
                            handleSearchInputChange(getField(Fields.searchInputValue), name);
                        }}
                        onSort={(name, item, index, sortKey) => {
                            handleSortByChange(name, sortKey);
                        }}

                        leftControls={(isCurrUserRoleAdmin() && !isBoolTrue(fromSearchPage)) && <ThemedIconButton
                            className={'fs-sm fw-semi-bold text-nowrap'}
                            outlined={true}
                            // sleek={true}
                            mr={2}
                            size={'28px'}
                            // text={`Create Folder`}
                            tooltip={`Create Folder`}
                            // href={GenRouteUrls.doc_createFolder}
                            icon={'bi bi-folder-plus'}
                            iconSize={'14px'}
                            variant={theme.primaryBlue}
                            // bgColor={theme.primaryBlue10}
                            onClick={() => updateField(Fields.openCreateFolderDialog, true)}
                        />}
                        rightComponent={<Flexbox className={''} justifyAt={'end'}
                            alignAt={'center'}
                            disabled={!checkNullArr(getRecordList())}>
                            <Select
                                id={Fields.folders}
                                wrapperClassName={`w-auto ms-2mt-1`}
                                className={`rounded-2 docsRightComponent`}
                                // Change by Vandit Tewari (28/10/24)- Earlier it was like the below one added an class to it to make changes in this and user here - docsRightComponent (in fusion_css.css)
                                // className={`rounded-2`}
                                variant={'primary'}
                                justifyAt={'start'}
                                style={{ height: '28px' }}
                                label={''}
                                textKey={'text'}
                                idKey={'id'}
                                required={true}
                                placeholder={'Select a folder'}
                                options={getField(Fields.folders, [])}
                                defOption={getField(Fields.selectedFolder)}
                                onChange={(e, text, value) => {
                                    // log('folder.Select.onChange::text:', text, "value:", value);
                                    updateField(Fields.selectedFolder, value);
                                }}
                            />
                        </Flexbox>}
                    />}

                {/*body*/}
                {<div className={'position-relative'}>
                    {/*record-list */}
                    {(isFieldTrue(Fields.isDataFound) && isFieldTrue(Fields.isDataFound)) &&
                        animator.setAnimationType(AnimationType.SLIDE_UP).renderChildren(
                            <div className={`w-100 m-0 p-0  rowx`}>
                                {getRecordListToRender().map((item, index) => {
                                    const recordId = getJsonValue(item, mf.recordId.column)
                                    return <div key={index + getJsonValue(item, mf.title.column)}
                                        className={'col-sm-12col-md-6 py-1 mb-2'}>

                                        <Section
                                            className={`mb-3x h-100 app-section hover`}
                                            borderless={false}
                                            roundedVariant={5}
                                            position={'relative'}
                                            borderColor={theme.text20}
                                            backgroundColor={theme.bg}
                                            contentClassName={'px-1'}>
                                            <div className={'w-100 position-relative m-0 p-0'}>
                                                {checkNullStr(getJsonValue(item, DOC_VECTOR_KEY)) && <Image
                                                    wrapperClassName={'jc-start mb-2'}
                                                    className={''}
                                                    // src={DOC_ICONS[randomInt(0, DOC_ICONS.length - 1)]}
                                                    src={getJsonValue(item, DOC_VECTOR_KEY)}
                                                    width={'auto'}
                                                    height={'50px'}
                                                    disableDrag={true}
                                                />}

                                                {/*title-link*/}
                                                <div className={'w-100 d-flex'}>
                                                    <Link
                                                        className={'fw-semi-bold m-0 p-0 text-start footer-link text-wrap'}
                                                        to={`${GenRouteUrls.doc_internalPage}${recordId}`}>
                                                        <Label className={'m-0 p-0 text-start w-100'} textAt={'start'}
                                                            style={{ lineHeight: 1.1 }}>
                                                            {highlightSearchMatchedText((getJsonValue(item, mf.title.column, DefStrNA)), SEARCH_BY_PARAMS.title.name)}
                                                        </Label>
                                                    </Link>
                                                </div>

                                                {/*right - controls*/}
                                                {/* <div className={"dash-modules-nav-bar m-0 p-0 rounded-right-4 xrounded-bottom-right-5 scroller scroller-x-auto none"}
                                                    style={{ backgroundColor: theme.primaryDark }}>
                                                    <Flexbox className={'px-3 w-auto pt-1'} justifyAt={'start'} alignAt={'center'}>
                                                        {Object.values(TABS).map((item, index) => {
                                                            return checkNullJson(item) && <Link
                                                                to={item.url}
                                                                className={`mdl-item cursor-pointer select-none px-3 pt-1x pb-1 py-0 ls-none rounded-top-4 d-flex jc-center al-center flex-nowrap text-nowrap border-none ${item.name == getField(Fields.activeTabName) ? 'active' : ''}`}
                                                                key={item.name + index}
                                                                style={{
                                                                    height: '34px', // backgroundColor: item.name == getField(Fields.activeTabName) ? theme.bg : theme.primary,
                                                                }}
                                                                onClick={(e) => {
                                                                    if (e.ctrlKey) {
                                                                        window.open(item.url, '_blank');
                                                                    } else {
                                                                        updateField(Fields.activeTabName, item.name);
                                                                        updateConfig(Keys.config_TabBarTab, item.name);
                                                                    }

                                                                    // updateField(Fields.activeTabName, item.name);
                                                                    // updateConfig(Keys.config_TabBarTab, item.name);
                                                                }}
                                                            >
                                                                <b className={"curve left-curve"}></b>
                                                                <b className={"curve right-curve"}></b>
                                                                <span
                                                                    className={`cursor-pointer x pyx-1 d-flex fd-row flex-nowrap al-center jc-center border-none text-center ${item.name == getField(Fields.activeTabName) ? 'fw-bold' : 'fw-normal'}`}
                                                                    style={{
                                                                        color: item.name == getField(Fields.activeTabName) ? theme.primary : theme.textWhite,
                                                                    }}
                                                                // iconClass={item.icon}
                                                                >
                                                                    {(getDefStr(item.renderName, item.name))}
                                                                </span>
                                                                // </span>
                                                            </Link>
                                                        })}
                                                    </Flexbox>
                                                </div> */}
                                                {/* - change made by Vandit (25/10/2024) commented this because delete and edit button are mixing with this title so moved these buttons down next to date div in aroun line - 387 */}


                                            </div>

                                            {/*desc*/}
                                            <div className={'mt-n1'}>
                                                <Label
                                                    className={'m-0 p-0 text-wrap mt-1x'}
                                                    color={theme.text}
                                                    textAt={'start'}>
                                                    <span className={'fw-semi-bold'}>{mf.desc.title}: </span>
                                                    <span>{
                                                        // checkNullArr(getRecordDescription(getJsonValue(item, mf.desc.column)))
                                                        // ? <ul className={'p-0 m-0 ls-none'}>
                                                        //     {getRecordDescription(getJsonValue(item, mf.desc.column)).map((item, index) => {
                                                        //         return (index < 3 && checkNullStr(item)) &&
                                                        //             <li key={index + item}>
                                                        //         <span className={'select-none fw-semi-bold'}
                                                        //               style={{color: theme.text50}}>{index + 1}{'. '}</span>
                                                        //                 <span>{highlightSearchMatchedText(item, SEARCH_BY_PARAMS.desc.name)}</span>
                                                        //             </li>
                                                        //     })}
                                                        // </ul>
                                                        // :
                                                        <span
                                                            className={'text-block'}>{highlightSearchMatchedText(limitStringWords(getDefStr(getRecordDescription(getJsonValue(item, mf.desc.column)) + '', DefStrNA), DESC_WORD_LIMIT, ' ', '...'), SEARCH_BY_PARAMS.desc.name)}</span>}
                                                    </span>
                                                </Label>
                                            </div>


                                            {/*author*/}
                                            <div>
                                                < Label
                                                    className={'m-0 p-0 text-wrap'}
                                                    color={theme.text}
                                                    textAt={'start'}>
                                                    <span className={'fw-semi-bold'}>{mf.author.title}: </span>
                                                    <span>{" "}{highlightSearchMatchedText(getJsonValue(item, mf.author.column, DefStrNA), SEARCH_BY_PARAMS.author.name)} </span>
                                                </Label>
                                            </div>

                                            {/* continue to line 333 - change made by Vandit (25/10/2024) */}
                                            {/* <div> - this is old version of the below div give it some inline style so date and these button can come next to each other*/}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                <Label
                                                    className={'m-0 p-0 text-wrap'}
                                                    color={theme.text}
                                                    textAt={'start'}>
                                                    <span className={'fw-semi-bold'}>{mf.date.title}: </span>

                                                    <span className={'text-nowrap'}>{" "}
                                                        {highlightSearchMatchedText(formatDateValue(getJsonValue(item, mf.date.column, DefStrNA)), SEARCH_BY_PARAMS.date.name)}
                                                    </span>
                                                </Label>

                                                {/* continue to line 333 - change made by Vandit (25/10/2024) */}
                                                {/* Pasted same code like top here no changes in the button code */}
                                                {/*right - controls*/}

                                                {isBoolTrue(showControls)
                                                    && <Flexbox className={''} justifyAt={'start'} alignAt={'center'}>
                                                        {/* edit-btn */}
                                                        {isCurrUserRoleAdmin() && <ThemedIconButton
                                                            className={''}
                                                            tooltip={'Edit'}
                                                            mx={2}
                                                            color={theme.primaryBlue}
                                                            bgColor={theme.textWhite}
                                                            hoverBgColor={theme.primaryBlue}
                                                            borderColor={theme.primaryBlue}
                                                            outlined={true}
                                                            icon={'bi bi bi-pencil'}
                                                            iconSize={'11px'}
                                                            size={'26px'}
                                                            href={`${GenRouteUrls.doc_editPage}${recordId}`}
                                                        />}


                                                        {/* delete-btn */}
                                                        {isCurrUserRoleAdmin() && <ThemedIconButton
                                                            className={''}
                                                            tooltip={'Delete'}
                                                            m={1}
                                                            ml={2}
                                                            variant={'red'}
                                                            bgColor={theme.textWhite}
                                                            hoverBgColor={'red'}
                                                            outlined={true}
                                                            icon={'bi bi bi-trash'}
                                                            iconSize={'11px'}
                                                            size={'26px'}
                                                            loadingVariant={CssVariant.danger}
                                                            asLoading={isFieldTrue(Fields.recordDeleteBtn_AsLoading) && getField(Fields.currRecordDeleteId) == recordId}
                                                            disabled={isFieldTrue(Fields.recordDeleteBtn_Disable) && getField(Fields.currRecordDeleteId) == recordId}
                                                            onClick={() => {
                                                                updateField(Fields.currRecordDeleteId, recordId);
                                                                updateField(Fields.showDeleteDialog, true);
                                                            }}
                                                        />}
                                                    </Flexbox>}
                                            </div>
                                        </Section>
                                    </div>
                                })}
                            </div>)}

                    {/*No search-found*/}
                    {((isSearchOngoing() && !searchListPending && checkNullArr(getRecordList()) && !checkNullArr(getRecordList(true)))) &&
                        <Flexbox className={'h-80 select-none'} justifyAt={'center'} alignAt={'center'} direction={'column'} wrap={'wrap'}>
                            <Label className={'m-1 w-100 fs-xxl fw-semi-bold'} color={theme.primary50}>
                                Search not found!
                            </Label>
                        </Flexbox>}

                    {<LoadingSection
                        labelClassName={'m-1 w-100 fs-xxl fw-semi-bold'}
                        heightClass={75}
                        color={theme.primary50}
                        success={isFieldTrue(Fields.isDataFound)}
                        loading={isFieldTrue(Fields.isDataLoading)}
                        msg={getField(Fields.dataLoadingMsg)}
                    />}
                </div>}

                {/* pagination */}
                {isBoolTrue(showPagination) && <RecordsPagination
                    id={TAG}
                    position={'sticky'}
                    bottom={'-.1rem'}
                    zIndex={1}
                    bgColor={theme.primary10Opaque}
                    scrollToTopOnPageChange={false}
                    totalRecords={getArrLen(getRecordList(true))}
                    totalRecordsToRender={getArrLen(getRecordListToRender())}
                    recordLimits={PAGINATION_RECORD_LIMITS}
                    onPageChange={(e, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                    onLimitChange={(newLimit, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newLimit:', newLimit, 'newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageLimit, newLimit);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                />}
            </div>
        </div>
    }

    /**
     * @example
     * const contentsString = "Contents: (1) From MDG to SDG (2) Meningococcal disease & its epidemiological pattern (3) Indian burden of IMD (4) Meningococcal Conjugate Vaccines (5) Efficacy, Immunogenicity & Effectiveness of MCV (6) Recommendations & global MCV uptake.";
     * const resultList = getRecordDescription(contentsString);
     * console.log(resultList);
     * //output:
     * [
     *   'From MDG to SDG',
     *   'Meningococcal disease & its epidemiological pattern',
     *   'Indian burden of IMD',
     *   'Meningococcal Conjugate Vaccines',
     *   'Efficacy',
     *   'Immunogenicity & Effectiveness of MCV',
     *   'Recommendations & global MCV uptake.'
     * ]
     */
    function getRecordDescription(desc) {
        if (checkNullStr(desc)) {
            desc = desc + '';
            // Remove "Contents:" from the beginning of the string (case-insensitive)
            const trimmedString = desc.replace(/^contents:/i, '').trim();
            // Split the remaining string by commas and/or parentheses to get individual items
            const items = trimmedString.split(/[,(]/);
            // Trim each item and remove any empty strings
            return items.map(item => trim((item + '').trim().replace(/^\d+\)/, '').replace(/\)$/, ''))).filter(item => checkNullStr(item));
        } else {
            return ''
        }
    }


    function createDeleteDialog() {
        return <ThemedConfirmDialog
            // updated by Vandit Tewari on (11/11/24) - added one more condition in open if isDeleting false means no delete operation processed then only open this delete model
            open={isFieldTrue(Fields.showDeleteDialog) && !isDeleting} 
            onCancel={() => updateField(Fields.showDeleteDialog, false)}
            onHide={() => updateField(Fields.currRecordDeleteId, null)}
            title={`Delete Document`}
            subMsg={getJsonValue(getRecordList().find(item => getField(Fields.currRecordDeleteId) == getJsonValue(item, mf.recordId.column)), mf.title.column)}
            operation={'delete'}
            target={'this document'}
            onConfirm={() => {
                handleRecordDelete();
            }}
        />
    }

    function createFolderFormDialog() {
        return <Doc_CreateFolder_FormDialog
            open={isFieldTrue(Fields.openCreateFolderDialog)}
            onHide={() => {
                // updateField(Fields.openCreateFolderDialog, false);
            }}
            onCancel={() => {
                updateField(Fields.openCreateFolderDialog, false);
            }}
            onSuccess={(fetchedData, message) => {
                updateField(Fields.openCreateFolderDialog, false);
                // log('createFolderFormDialog.onSuccess.fetchedData:', fetchedData, 'message:', message);
            }}
        />
    }

    //======================UI Functions end=====================

    function handleRecordDelete() {
        const fun = 'handleRecordDelete';
        const recordId = getField(Fields.currRecordDeleteId);

        if (checkNullStr(recordId)) {
            updateField(Fields.recordDeleteBtn_Disable, true);
            updateField(Fields.recordDeleteBtn_AsLoading, true);
            // added by Vandit Tewari on (11/11/24) - set it true when delete process start to not conflict with other delete operation when delete ongoing not start
            setIsDeleting(true);  

            try {
                const REQUEST_OPTIONS = { recordId: recordId };
                sendRequest({
                    reqUrl: ReqUrls.doc__mainPage_delete_record,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        updateField(Fields.showDeleteDialog, false);  // Close dialog on success/failure

                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');

                        if (success) {
                            toast(message, CssVariant.success);
                            const recordList = getRecordList().filter((item) => recordId != getJsonValue(item, mf.recordId.column));
                            updateField(Fields.recordList, recordList);
                            if (onRecordsUpdate) onRecordsUpdate(recordList);

                            if (!checkNullArr(recordList)) {
                                updateField(Fields.isDataFound, false);
                                updateField(Fields.dataLoadingMsg, "No records found!");
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        // added by Vandit Tewari on (11/11/24) - set it false when operation completed so all the other operations of delete can be processed
                        setIsDeleting(false);  // Operation complete, set isDeleting to false
                        updateField(Fields.currRecordDeleteId, null);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        // added by Vandit Tewari on (11/11/24) - set it false if operation fails due to any of the follow reason in that case also have to close it
                        setIsDeleting(false);  // Operation complete with error, set isDeleting to false
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                setIsDeleting(false);  // Operation failed, set isDeleting to false
                updateField(Fields.recordDeleteBtn_Disable, false);
                updateField(Fields.recordDeleteBtn_AsLoading, false);
            }
        } else {
            toast('Record-id not found!', CssVariant.danger);
        }
    }

    function fetchPanelData() {
        const fun = 'fetchPanelData:';
        try {
            // log(fun, 'isDataFetched:', isFieldTrue(Fields.isDataFetched));
            // if (!isFieldTrue(Fields.isDataFetched) && checkNullJson(loadingStatus)) {
            log(fun, 'recordList:', recordList, 'loadingStatus:', loadingStatus);
            updateField(Fields.isDataFetched, true);
            updateField(Fields.isDataLoading, true);
            // createTimeout(() => {
            const success = isJsonValueTrue(loadingStatus, 'success');
            const records = checkNullArr(recordList) ? recordList.map(item => {
                return {
                    ...item,
                    [DOC_VECTOR_KEY]: getRecordDisplayIcon(item)
                }
            }) : [];
            // log(fun, 'records:', records);
            updateField(Fields.recordList, records);
            updateField(Fields.isDataLoading, !isJsonValueTrue(loadingStatus, 'loaded'));
            updateField(Fields.isDataFound, success);

            updateField(Fields.dataLoadingMsg, getJsonValue(loadingStatus, 'msg'));

            if (checkNullStr(searchString) && isBoolTrue(fromSearchPage) && !isFieldTrue(Fields.isSearchedStringSet) && success) {
                // log('useEffect.searchString:', searchString);
                handleSearchInputChange(searchString, SEARCH_BY_PARAMS.anything.name, records);
            }

            if (checkNullArr(records) && isJsonValueTrue(loadingStatus, 'success')) {
                let folders = records.map((item) => {
                    return {
                        id: getJsonValue(item, mf.folderId.column),
                        text: getJsonValue(item, mf.folderName.column)
                    };
                });
                folders = dropJsonListDuplicates(folders, 'id');
                folders.unshift({ id: ALL_FOLDERS, text: ALL_FOLDERS });
                // log(fun, 'records.folders:', folders);

                updateField(Fields.folders, folders)
                updateField(Fields.selectedFolder, getJsonValue(getArrIndexValue(folders, 0), 'id'));
            }
            // }, 1500);
            // }
        } catch (error) {
            logErr(fun, error);
        }

        function getRecordDisplayIcon(record) {
            const docFileExt = (getJsonValue(record, mf.docFileExt.column) + '').trim().toLowerCase();
            // log('getRecordDisplayIcon.record:', record, ' | docFileExt:', docFileExt, ' | fileExist:', isJsonValueTrue(record, mf.fileExist.column));
            if (checkNullStr(docFileExt) && isJsonValueTrue(record, mf.fileExist.column)) {
                if (docFileExt.endsWith('.pdf')) {
                    return PDF_ICON;
                } else if (docFileExt.endsWith('.ppt') || docFileExt.endsWith('.pptx')) {
                    return PPT_ICON;
                }
            }
            // return DOC_ICONS[randomInt(0, DOC_ICONS.length - 1)];
            return null;//No need for random icons (from Sir, 12Aug2024)
        }
    }

    //======================Util Functions start=====================
    function handleSearchInputChange(inputValue, searchByOption = getField(Fields.searchBySelectedOption), recordList) {
        const fun = 'handleSearchInputChange:';
        if (isBoolTrue(fromSearchPage)) {
            performOps();
        } else {
            startSearchListTransition(() => {
                performOps();
            });
        }

        function performOps() {
            try {
                updateField(Fields.searchInputValue, inputValue);

                // Filter search query based on selected search filter
                const inputValueL = (inputValue + '').toLowerCase();
                recordList = checkNullArr(recordList) ? recordList : getRecordList();
                if (checkNullStr(inputValue) && checkNullArr(recordList)) {
                    recordList = recordList.map((item) => {
                        const title = (getJsonValue(item, mf.title.column) + '').toLowerCase();
                        const desc = (getJsonValue(item, mf.desc.column) + '').toLowerCase();
                        const author = (getJsonValue(item, mf.author.column) + '').toLowerCase();
                        // const date = (getJsonValue(item, mf.date.column) + '').toLowerCase();
                        const date = (formatDateValue(getJsonValue(item, mf.date.column)) + '').toLowerCase();

                        let matched = false;
                        switch (searchByOption) {
                            case SEARCH_BY_PARAMS.anything.name:
                                matched = (title + desc + author + date).includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.title.name:
                                matched = title.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.desc.name:
                                matched = desc.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.author.name:
                                matched = author.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.date.name:
                                matched = date.includes(inputValueL);
                                break;
                        }
                        return {
                            ...item,
                            searched: matched
                        }
                    });
                    // log(fun, 'inputValue:', inputValue, 'searchBy:', getField(Fields.searchBySelectedOption), 'recordList', recordList);
                }
                // log(fun, 'inputValue:', inputValue, 'searchByOption:', searchByOption, 'recordList:', recordList);
                updateField(Fields.recordList, recordList);
            } catch (error) {
                logErr(fun, error);
                toast('Search failed, please try again', CssVariant.danger);
            }
        }
    }

    function handleSortByChange(name, sortKey) {
        const fun = 'handleSortByChange:';
        try {
            startSortListTransition(() => {
                const sortOption = getField(Fields.sortBySelectedOption);
                const order = name == sortOption ? !isFieldTrue(Fields.isSortByOrderAsc) : isFieldTrue(Fields.isSortByOrderAsc);

                updateField(Fields.isSortByOrderAsc, order);
                updateField(Fields.sortBySelectedOption, name);

                updateField(Fields.recordList, sortJsonListAdvance(shuffleList(getRecordList()), sortKey, order));
            });
        } catch (error) {
            toast('Sort failed, please try again', CssVariant.danger);
            logErr(fun, error);
        }
    }

    function highlightSearchMatchedText(text, searchByParam) {
        const can = (getField(Fields.searchBySelectedOption) == SEARCH_BY_PARAMS.anything.name || getField(Fields.searchBySelectedOption) == searchByParam);
        return can ? getHighlightedMatchedText(text, getField(Fields.searchInputValue)) : text;
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({ ...prevState, [field]: value }));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function getRecordList(searchedOnly = false) {
        if (checkNullArr(getField(Fields.recordList))) {
            if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
                // return getField(Fields.recordList).filter(item => isJsonValueTrue(item, 'searched'))
                // return getField(Fields.recordList).filter(item => isJsonValueTrue(item, 'searched')).filter(item => getJsonValue(item, mf.folderId.column) == getField(Fields.selectedFolder))ALL_FOLDERS
                return getField(Fields.selectedFolder) == ALL_FOLDERS
                    ? getField(Fields.recordList).filter(item => isJsonValueTrue(item, 'searched'))
                    : getField(Fields.recordList).filter(item => isJsonValueTrue(item, 'searched')).filter(item => getJsonValue(item, mf.folderId.column) == getField(Fields.selectedFolder))
            }
            // return getField(Fields.recordList).filter(item => getJsonValue(item, mf.folderId.column) == getField(Fields.selectedFolder));
            return getField(Fields.selectedFolder) == ALL_FOLDERS
                ? getField(Fields.recordList)
                : getField(Fields.recordList).filter(item => getJsonValue(item, mf.folderId.column) == getField(Fields.selectedFolder));
            // return getField(Fields.recordList);
        }
        return [];
        // let list = checkNullArr(getField(Fields.recordList)) ? getField(Fields.recordList) : [];
        // if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
        //     list = list.filter(item => isJsonValueTrue(item, 'searched'));
        // }
        // return list;
    }

    function getRecordListToRender() {
        return getRecordList(true).slice((getField(Fields.pagination_currPageNum) - 1) * getField(Fields.pagination_currPageLimit), getField(Fields.pagination_currPageNum) * getField(Fields.pagination_currPageLimit));
    }

    function isSearchOngoing() {
        return checkNullStr(getField(Fields.searchInputValue));
    }


    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});

export default DocPanel;
